import { useState } from "react";
import { Loader2, CreditCard, Sparkles, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { centsMoney, fmtDay, planInfo } from "@/lib/plans";
import { PlanPicker } from "./PlanPicker";
import { SquareCard } from "./SquareCard";

const every = (cadence) => (cadence === "yearly" ? "year" : "month");

export const FoundingNote = () => (
  <p className="flex items-center gap-2 rounded-lg bg-primary/10 text-primary px-3 py-2 text-xs font-semibold" data-testid="billing-founding-note">
    <Sparkles className="w-4 h-4" /> Founding customer price: 30% off for life.
  </p>
);

const TestMode = ({ square }) => square.env === "sandbox" && (
  <p className="flex items-center gap-2 rounded-lg bg-amber-500/10 text-amber-700 px-3 py-2 text-xs" data-testid="billing-test-mode">
    <FlaskConical className="w-4 h-4 shrink-0" /> Test mode: no real money moves. Use card 4111 1111 1111 1111, any future date, CVV 111 and postcode 12345.
  </p>
);

const NotReady = () => (
  <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid="billing-not-ready">
    Card payments are being switched on. Please check back soon.
  </p>
);

const Submit = ({ busy, children, ...props }) => (
  <Button disabled={busy} className="rounded-full bg-primary hover:bg-primary/90 text-white" {...props}>
    {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {children}
  </Button>
);

// No plan yet: pick a plan, add a card, start the subscription (first charge after the trial).
export const StartPlan = ({ b, sel, setSel, busy, run, cardRef, contact }) => {
  const trial = b.status === "trial";
  const plan = b.plans.find((p) => p.id === sel.plan);
  const amount = plan ? centsMoney(plan.charge[sel.cadence]) : "";
  const go = () => run(async () => api.subscribe({ ...sel, card_token: await cardRef.current.tokenize() }), "Your plan is set up. Thanks!");
  return (
    <div className="space-y-4" data-testid="billing-start">
      {b.founding_price && <FoundingNote />}
      <PlanPicker billing={b} value={sel} onChange={setSel} />
      {!b.square.ready ? <NotReady /> : (
        <>
          <TestMode square={b.square} />
          <SquareCard ref={cardRef} square={b.square} contact={contact} />
          <p className="text-xs text-muted-foreground" data-testid="billing-charge-note">
            {trial ? `Nothing is charged until your trial ends on ${fmtDay(b.trial_ends_at)}. Then ${amount} incl. GST every ${every(sel.cadence)}.`
              : `${amount} incl. GST is charged today, then every ${every(sel.cadence)}.`} Cancel any time.
          </p>
          <Submit busy={busy} onClick={go} disabled={busy || !plan} data-testid="billing-subscribe-btn">
            {trial ? "Save card and choose plan" : `Pay ${amount} and start`}
          </Submit>
        </>
      )}
    </div>
  );
};

// Has a plan: switch plan, replace the card or cancel.
export const ManagePlan = ({ b, sel, setSel, busy, run, cardRef, contact }) => {
  const [mode, setMode] = useState(null);
  const cur = b.next || b;
  const same = sel.plan === cur.plan && sel.cadence === cur.cadence;
  const toggle = (m) => setMode((x) => (x === m ? null : m));
  return (
    <div className="space-y-4" data-testid="billing-manage">
      {b.card && <p className="text-sm flex items-center gap-2" data-testid="billing-card-on-file"><CreditCard className="w-4 h-4 text-muted-foreground" /> {b.card.brand || "Card"} ending {b.card.last4}</p>}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="rounded-full" onClick={() => toggle("change")} disabled={!!b.cancel_date} data-testid="billing-change-btn">Change plan</Button>
        <Button variant="outline" className="rounded-full" onClick={() => toggle("card")} disabled={!b.square.ready} data-testid="billing-card-btn">Update card</Button>
        {!b.cancel_date && <Button variant="ghost" className="rounded-full text-destructive hover:text-destructive" onClick={() => toggle("cancel")} data-testid="billing-cancel-btn">Cancel plan</Button>}
      </div>
      {b.cancel_date && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-secondary/60 p-3" data-testid="billing-ending-note">
          <p className="text-sm flex-1 min-w-[200px]">Your plan ends on {fmtDay(b.cancel_date)}. Resume it to keep going, or pick a new plan after it ends.</p>
          <Submit busy={busy} onClick={() => run(api.resumePlan, "Plan resumed")} data-testid="billing-resume-btn">Resume plan</Submit>
        </div>
      )}
      {mode === "change" && (
        <div className="space-y-3">
          <PlanPicker billing={b} value={sel} onChange={setSel} />
          <p className="text-xs text-muted-foreground">Extra depots and crew are available straight away. The new price starts from your next bill.</p>
          <Submit busy={busy} disabled={busy || same} onClick={() => run(() => api.changePlan(sel), "Plan changed")} data-testid="billing-change-submit">
            Switch to {planInfo(sel.plan)?.name} ({sel.cadence})
          </Submit>
        </div>
      )}
      {mode === "card" && (
        <div className="space-y-3">
          <TestMode square={b.square} />
          <SquareCard ref={cardRef} square={b.square} contact={contact} />
          <Submit busy={busy} onClick={() => run(async () => api.updateCard(await cardRef.current.tokenize()), "Card updated")} data-testid="billing-card-submit">Save new card</Submit>
        </div>
      )}
      {mode === "cancel" && (
        <div className="rounded-lg border border-destructive/40 p-4 space-y-3" data-testid="billing-cancel-confirm-box">
          <p className="text-sm">{b.booked
            ? `Cancel your booked plan? Nothing has been charged. You'll stay on the free trial until ${fmtDay(b.trial_ends_at)} and can pick a plan any time before then.`
            : "Cancel your plan? It stays on until the end of the time you've paid for. After that the office dashboard locks, but your data is kept."}</p>
          <Button variant="destructive" className="rounded-full" disabled={busy} onClick={() => run(api.cancelPlan, "Plan cancelled")} data-testid="billing-cancel-confirm">Yes, cancel my plan</Button>
        </div>
      )}
    </div>
  );
};
